import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { secondsToMinutes } from 'date-fns';
import StarIcon from '@mui/icons-material/Star';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { Box } from '@mui/system';
import { Chip, Divider, IconButton, Stack, Typography } from '@mui/material';
import FavoriteTwoToneIcon from '@mui/icons-material/FavoriteTwoTone';
import FavoriteIcon from '@mui/icons-material/Favorite';

import { useDispatch, useSelector } from 'react-redux';
import useClasses from '../../../hooks/legacy/useClasses';
import getLocationsDistance from '../../../util/GetLocationsDistance';

import styles from './RestaurantCard.style';
import { getRatings } from '../../../util/Helpers';
import { setFavoriteRequest } from '../../../redux/legacy/reducers/ducks/legacy/ProfileDuck';
import { MIN_SHOW_RATING_REVIEWS } from '../../../util/Constants';

function RestaurantCard({
  data,
  onRestaurantClick,
  address,
  isMapOpen,
  variant,
  orderType,
}) {
  const classes = useClasses(styles);
  const dispatch = useDispatch();

  const image = data?.media?.consumer_restaurant_list_logo || '';

  const [cardVariant, setCardVariant] = useState('list');
  const [distance, setDistance] = useState(0);

  const [fees, setFees] = useState(0);

  const { accessToken, history } = useSelector(
    ({ authorization, profile }) => ({
      accessToken: authorization.accessToken,
      history: profile?.orders,
    })
  );

  const restaurantClicked = (restaurantData) => {
    onRestaurantClick(restaurantData.profile);
  };

  useEffect(() => {
    setDistance(
      getLocationsDistance(
        address?.latitude,
        address?.longitude,
        data?.location?.latitude,
        data?.location?.longitude
      )
    );

    setFees(
      Object.values(data?.fees || {}).reduce((acc, fee) => acc + fee, 0) || 0
    );
  }, [
    address?.latitude,
    address?.longitude,
    data?.delays?.delivery,
    data?.delays?.takeout,
    data?.fees,
    data?.location?.latitude,
    data?.location?.longitude,
    data?.rating,
  ]);

  useEffect(() => {
    switch (variant) {
      case 'list':
        setCardVariant('list');
        break;
      case 'carousel':
        setCardVariant('carousel');
        break;
      case 'map-full-screen':
        setCardVariant('map-full-screen');
        break;
      case 'map-popUp':
        setCardVariant('map-popUp');
        break;
      default:
        setCardVariant('list');
    }
  }, [variant]);

  const delayWindow =
    orderType?.value === 'delivery'
      ? {
          min: secondsToMinutes(data?.delays?.delivery) || 0,
          max:
            (secondsToMinutes(data?.delays?.delivery) || 0) +
            (secondsToMinutes(data?.windows?.delivery) || 0),
        }
      : {
          min: secondsToMinutes(data?.delays?.takeout) || 0,
          max:
            (secondsToMinutes(data?.delays?.takeout) || 0) +
            (secondsToMinutes(data?.windows?.takeout) || 0),
        };

  const onFavorite = useCallback(() => {
    dispatch(
      setFavoriteRequest({
        id: data.profile,
        favorite: !data.user_attributes?.favorite,
        type: 'restaurant',
      })
    );
  }, [dispatch, data, accessToken]);

  return (
    <Box
      className={clsx({
        [classes.card]: true,
        [classes[cardVariant]]: true,
        [classes[`${cardVariant}-mapOpen`]]: isMapOpen,
      })}
      data-test-id="promoCard"
      id={data.profile}
      sx={{ position: 'relative' }}
    >
      {accessToken && (
        <IconButton
          data-test-id="favorite-icon-button"
          onClick={onFavorite}
          sx={{ position: 'absolute', top: 0, right: 10 }}
        >
          {history?.length > 0 &&
            (data.user_attributes?.favorite ? (
              <FavoriteIcon className={classes.activeFavoriteIcon} />
            ) : (
              <FavoriteTwoToneIcon className={classes.favoriteIcon} />
            ))}
        </IconButton>
      )}
      <Link
        to={`/restaurant/${data.profile}`}
        className={clsx({
          [classes.card]: true,
          [classes[`cardItem-${cardVariant}`]]: true,
          [classes[`cardItem-${cardVariant}-mapOpen`]]: isMapOpen,
        })}
        key={data.profile}
        onClick={() => restaurantClicked(data)}
        data-test-id="filtered-promo-card"
        rel="nofollow"
      >
        <Box
          sx={{
            borderRadius: '12px',
            width: '100%',
            height: 163,
            objectFit: 'cover',
            objectPosition: 'center',
            backgroundImage: `url(${image})`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'contain',
            backgroundOrigin: 'padding-box',
          }}
          data-test-id="promo-card-image"
        />
        {!data.open && (
          <>
            <Box
              sx={{
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                borderRadius: '12px',
                width: '100%',
                height: 163,
                objectFit: 'cover',
                position: 'absolute',
                bgcolor: 'rgba(54, 54, 54, 0.65)',
              }}
            />
            <Box
              sx={{
                top: '10px',
                left: '15px',
                textAlign: 'left',
                position: 'absolute',
              }}
            >
              <Chip
                size="large"
                color="primary"
                label="Order Ahead"
                sx={{ color: 'white' }}
              />
            </Box>
          </>
        )}

        <Box display="flex" flexDirection="column">
          <Typography
            data-test-id="restaurant-card-name"
            variant="body1"
            fontWeight={600}
            mt={2}
            lineHeight={1.5}
          >
            {data.name}
          </Typography>
          <Stack
            direction="row"
            divider={<Divider orientation="vertical" flexItem />}
            spacing={1}
          >
            {data?.price_level && (
              <Typography color="primary.dark" variant="body2">
                {'$'.repeat(data?.price_level)}
              </Typography>
            )}
            {data?.ratings?.reviews &&
              parseInt(data.ratings.reviews, 10) >= MIN_SHOW_RATING_REVIEWS && (
                <>
                  <StarIcon className={classes.starIcon} />
                  <Typography color="primary.dark" variant="body2">
                    {data?.ratings?.stars
                      ? getRatings(data.ratings.stars)
                      : '-'}{' '}
                    ({data?.ratings?.reviews || 0})
                  </Typography>
                </>
              )}
            <Typography
              color="primary.dark"
              variant="body2"
              data-test-id="restaurant-card-delay"
            >
              {`${delayWindow.min}-${delayWindow.max} min`}
            </Typography>
            {isMapOpen ? (
              <Typography color="primary.dark" variant="body2">
                {Number(distance).toFixed(2)} miles
              </Typography>
            ) : (
              <Box>
                {orderType.value === 'delivery' && (
                  <Typography color="primary.dark" variant="body2">
                    {fees ? `$${fees}` : 'No delivery fee'}
                  </Typography>
                )}
                {orderType.value === 'takeout' && (
                  <Typography color="primary.dark" variant="body2">
                    {fees ? `$${fees}` : 'No fee'}
                  </Typography>
                )}
              </Box>
            )}
          </Stack>

          <Box display="flex" justifyContent="space-between">
            {data?.tags?.length ? (
              <Box className={classes.tags} data-test-id="restaurant-card-tags">
                {data?.tags?.map((tag) => (
                  <Typography
                    data-test-id="tag"
                    className={classes.tag}
                    key={tag}
                  >
                    {tag}
                  </Typography>
                ))}
              </Box>
            ) : null}
          </Box>
        </Box>
      </Link>
    </Box>
  );
}

RestaurantCard.propTypes = {
  data: PropTypes.shape({
    address: PropTypes.string,
    open: PropTypes.bool,
    categories: PropTypes.arrayOf(PropTypes.string),
    city: PropTypes.string,
    delays: PropTypes.shape({
      takeout: PropTypes.number,
      delivery: PropTypes.number,
    }),
    description: PropTypes.string,
    distance: PropTypes.number,
    features: PropTypes.arrayOf(PropTypes.string),
    fees: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.number),
      PropTypes.objectOf(PropTypes.number),
    ]),
    id: PropTypes.number,
    keywords: PropTypes.arrayOf(PropTypes.string),
    location: PropTypes.shape({
      latitude: PropTypes.number,
      longitude: PropTypes.number,
    }),
    media: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.string),
      PropTypes.objectOf(PropTypes.string),
    ]),
    modes: PropTypes.shape({
      delivery: PropTypes.bool,
      reservations: PropTypes.bool,
      takeout: PropTypes.bool,
    }),
    name: PropTypes.string,
    phone: PropTypes.string,
    profile: PropTypes.string,
    state: PropTypes.string,
    tags: PropTypes.arrayOf(PropTypes.string),
    rating: PropTypes.number,
    text: PropTypes.objectOf(PropTypes.string),
    windows: PropTypes.shape({
      takeout: PropTypes.number,
      delivery: PropTypes.number,
    }),
    zipcode: PropTypes.string,
    price_level: PropTypes.number,
    user_attributes: PropTypes.shape({
      favorite: PropTypes.bool,
    }),
    ratings: PropTypes.shape({
      stars: PropTypes.string,
      reviews: PropTypes.string,
    }),
  }).isRequired,
  isMapOpen: PropTypes.bool,
  onRestaurantClick: PropTypes.func.isRequired,
  address: PropTypes.shape({
    latitude: PropTypes.number,
    longitude: PropTypes.number,
  }),
  variant: PropTypes.string,
  orderType: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
    text: PropTypes.string,
  }).isRequired,
};

RestaurantCard.defaultProps = {
  isMapOpen: false,
  address: {},
  variant: '',
};

export default RestaurantCard;
